import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './TagsFilter.css';

const TagsFilter = ({ selectedTags = [], onTagsChange, popularTags = [] }) => {
  const { t } = useTranslation(['discovery']);
  const [tagsInput, setTagsInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const inputRef = useRef(null);

  // Инициализируем теги из пропсов
  useEffect(() => {
    setTagsInput(selectedTags.join(', '));
  }, [selectedTags]);

  // Обработка изменения ввода
  const handleInputChange = (e) => {
    const value = e.target.value;
    setTagsInput(value);

    // Фильтруем подсказки на основе ввода
    if (value.trim()) {
      const lastTag = value.split(',').pop().trim().toLowerCase();
      if (lastTag.length > 0) {
        const filtered = popularTags.filter(
          tag => tag.toLowerCase().includes(lastTag) && 
          !selectedTags.includes(tag) // Исключаем уже выбранные теги
        );
        setFilteredSuggestions(filtered);
        setShowSuggestions(filtered.length > 0);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
  };

  // Применение тегов при потере фокуса или нажатии Enter
  const handleApplyTags = () => {
    const newTags = tagsInput
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 2) // Минимум 3 символа
      .filter((tag, index, self) => self.indexOf(tag) === index); // Удаление дубликатов

    // Ограничиваем количество тегов
    const limitedTags = newTags.slice(0, 5);

    // Применяем изменения немедленно
    onTagsChange(limitedTags);
    setShowSuggestions(false);
  };

  // Обработка нажатия Enter или потери фокуса
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApplyTags();
    }
  };

  const handleInputBlur = () => {
    // Небольшая задержка чтобы клики по подсказкам успели сработать
    setTimeout(() => {
      handleApplyTags();
      setShowSuggestions(false);
    }, 150);
  };

  // Выбор подсказки
  const handleSuggestionClick = (suggestion) => {
    const tagParts = tagsInput.split(',');
    tagParts.pop(); // Удаляем последний элемент (текущий ввод)

    const newTagsInput = tagParts.length > 0
      ? `${tagParts.join(',')}, ${suggestion}`
      : suggestion;

    setTagsInput(newTagsInput);
    setShowSuggestions(false);
    
    // Применяем изменения сразу
    const newTags = newTagsInput
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 2)
      .filter((tag, index, self) => self.indexOf(tag) === index)
      .slice(0, 5);
    
    onTagsChange(newTags);
    inputRef.current.focus();
  };

  // Удаление тега
  const removeTag = (tagToRemove) => {
    const updatedTags = selectedTags.filter(tag => tag !== tagToRemove);
    setTagsInput(updatedTags.join(', '));
    onTagsChange(updatedTags);
  };

  // Очистка всех тегов
  const clearAllTags = () => {
    setTagsInput('');
    onTagsChange([]);
  };

  // Обработка клика вне компонента
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="tags-filter">
       <div className="tags-filter__header">

      </div>

      <div className="tags-filter__input-container">


        <div className="tags-filter__input-wrapper" ref={inputRef}>
          <input
            id="tags-input"
            className="tags-filter__input"
            type="text"
            value={tagsInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleInputBlur}
            placeholder={t('channelDiscovery.tagsFilter.placeholder', 'Введите теги через запятую')}
          />

          {showSuggestions && (
            <div className="tags-filter__suggestions">
              {filteredSuggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className="tags-filter__suggestion"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Отображение выбранных тегов */}
      {selectedTags.length > 0 && (
        <div className="tags-filter__selected-section">
          <div className="tags-filter__selected-header">
            <span className="tags-filter__selected-title">
              {t('channelDiscovery.tagsFilter.selectedTags', 'Выбранные теги')}
            </span>
            <span className="tags-filter__selected-count">
              {selectedTags.length}
            </span>
          </div>
          
          <div className="tags-filter__selected-tags">
            {selectedTags.map((tag, index) => (
              <div key={index} className="tags-filter__tag">
                <span className="tags-filter__tag-text">{tag}</span>
                <button
                  className="tags-filter__tag-remove"
                  onClick={() => removeTag(tag)}
                  title={`Удалить тег: ${tag}`}
                >
                  ×
                </button>
              </div>
            ))}

            {selectedTags.length > 0 && (
              <div className="tags-filter__actions">
                <button
                  className="tags-filter__clear-all"
                  onClick={clearAllTags}
                >
                  {t('channelDiscovery.tagsFilter.clearAll', 'Очистить все')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagsFilter;